/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a wall of KPIWidgets
 * required params:
 *  - data
 *  Example : 
 *  [
        { label: "Property 1", value: "115", status: "ok" },
        { label: "Property 2", value: "8", status: "ko" }
    ];
 */

import React, { Fragment } from "react";
import PropTypes from "prop-types";
import BackgroundPanel from "./BackgroundPanel";
import SelectionButton from "./SelectionButton";
import Text from "./Text";

function PanelsBoard({
    panel = false,
    children,
    height,
    width,
    columns = 2,
    rows = 1,
    title = false,
    selection = false,
    currentSelection,
    setSelectionChoices,
    selectionChoices,
    zOffset = true,
    repositionate = true,
    textSizeDecimal,
    aframeClasses,
    ...otherAttributes
}) {
    const minDimension = width > height ? height : width;
    const BOARD_GRID_PADDING = panel ? minDimension / 20 : 0;
    const BOARD_GRID_HORIZONTAL_ELEMENT_LENGTH =
        (width - 2 * BOARD_GRID_PADDING) / columns;
    const BOARD_GRID_VERTICAL_ELEMENT_LENGTH =
        (height - (title ? 3 : 2) * BOARD_GRID_PADDING) / rows;

    const BOARD_GRID_ELEMENT_PADDING = minDimension / 100;

    const prepareData = () => {
        if (!children.length) return [[children]];
        let n = rows;
        if (n < 2) return [children];
        let len = children.length,
            out = [],
            i = 0,
            size;

        if (len % n === 0) {
            size = Math.floor(len / n);
            while (i < len) {
                out.push(children.slice(i, (i += size)));
            }
        } else {
            while (i < len) {
                size = Math.ceil((len - i) / n--);
                out.push(children.slice(i, (i += size)));
            }
        }
        return out;
    };

    const positionatePanels = () => {
        const preparedData = prepareData();
        const maxWidth =
            (columns * BOARD_GRID_HORIZONTAL_ELEMENT_LENGTH) /
            (preparedData[0].length ? preparedData[0].length : 1);

        return preparedData.map((elem, i) => {
            const elemWidth =
                (columns * BOARD_GRID_HORIZONTAL_ELEMENT_LENGTH) /
                (elem.length ? elem.length : 1);
            return elem.map((e, j) => {
                const x =
                    j * elemWidth +
                    0.5 * elemWidth +
                    columns * BOARD_GRID_HORIZONTAL_ELEMENT_LENGTH * -0.5;

                const y =
                    i * BOARD_GRID_VERTICAL_ELEMENT_LENGTH +
                    0.5 * BOARD_GRID_VERTICAL_ELEMENT_LENGTH +
                    rows * BOARD_GRID_VERTICAL_ELEMENT_LENGTH * -0.5;

                const key = preparedData[i - 1]
                    ? i * preparedData[i - 1].length + j
                    : i * preparedData[i].length + j;

                return React.cloneElement(e, {
                    key: key,
                    width: elemWidth - 2 * BOARD_GRID_ELEMENT_PADDING,
                    maxWidth,
                    height:
                        BOARD_GRID_VERTICAL_ELEMENT_LENGTH -
                        2 * BOARD_GRID_ELEMENT_PADDING,
                    position: `${x} ${y} 0`,
                    backgroundColor: "#262626",
                    opacity: 1
                });
            });
        });
    };
    const elem =
        children.length !== 0 ? (
            <>
                {title && (
                    <Text
                        text={title}
                        width={(width * 4) / 5}
                        sizeDecimal={
                            textSizeDecimal ||
                            (width > height
                                ? 60 / ((width * 4) / 5)
                                : 40 / height)
                        }
                        side="double"
                        align="left"
                        position={`${-width / 2 + BOARD_GRID_PADDING} ${
                            height / 2 -
                            BOARD_GRID_PADDING -
                            BOARD_GRID_PADDING / 2
                        } ${width / 200}`}
                    />
                )}
                {selection && (
                    <SelectionButton
                        id={selectionChoices[0] + width}
                        width={width / 5}
                        height={BOARD_GRID_PADDING}
                        current={currentSelection}
                        choices={selectionChoices}
                        setCurrent={setSelectionChoices}
                        aframeClasses={aframeClasses}
                        position={`${
                            width / 2 - BOARD_GRID_PADDING - width / 10
                        } ${
                            height / 2 -
                            BOARD_GRID_PADDING -
                            BOARD_GRID_PADDING / 2
                        } ${width / 100}`}
                        textSizeDecimal={15 / (width / 5)}
                    />
                )}
                <a-entity
                    position={`0 ${title ? -BOARD_GRID_PADDING : 0} ${
                        zOffset ? width / 200 : 0
                    }`}
                >
                    {repositionate ? positionatePanels() : children}
                </a-entity>
            </>
        ) : null;

    return (
        <a-entity class="PanelsBoard" {...otherAttributes}>
            {panel ? (
                <BackgroundPanel
                    backgroundColor={"#121212"}
                    width={width}
                    height={height}
                >
                    {elem}
                </BackgroundPanel>
            ) : (
                <>{elem}</>
            )}
        </a-entity>
    );
}

PanelsBoard.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
};

export default PanelsBoard;
